<template>
  <header class="header" :class="{ 'header--transparent': transparent }">
    <div class="logo">
      <img
        src="@/assets/images/antojese-travel-blanco.png"
        alt="Antojese Travel Logo"
      />
    </div>
    <nav class="desktop-nav">
      <!-- Selector de Moneda -->
      <div class="currency-menu-container" v-if="showCurrencySelector">
        <div 
          class="currency-trigger" 
          :class="{ 'currency-trigger--disabled': !canChangeCurrency }"
          @click="canChangeCurrency ? toggleCurrencyMenu() : null"
        >
          <img
            :src="selectedCurrency === 'COP' ? coFlag : usFlag"
            class="currency-flag-icon"
            alt="Moneda"
          />
          <span class="currency-label-desktop">{{ selectedCurrency }}</span>
          <!-- Solo mostramos la flecha si se permite el cambio -->
          <span v-if="canChangeCurrency" class="arrow-icon" :class="{ 'arrow-icon--open': isCurrencyMenuOpen }">▾</span>
        </div>
        <transition name="fade">
          <div v-if="isCurrencyMenuOpen && canChangeCurrency" class="currency-dropdown-menu">
            <div 
              class="currency-option-item" 
              :class="{ 'currency-option-item--active': selectedCurrency === 'COP' }"
              @click="selectCurrency('COP')"
            >
              <img :src="coFlag" class="currency-flag-icon" alt="COP" />
              <span>Peso (COP)</span>
            </div>
            <div 
              class="currency-option-item" 
              :class="{ 'currency-option-item--active': selectedCurrency === 'USD' }"
              @click="selectCurrency('USD')"
            >
              <img :src="usFlag" class="currency-flag-icon" alt="USD" />
              <span>Dólar (USD)</span>
            </div>
          </div>
        </transition>
      </div>
      <!-- Menú para usuarios no autenticados -->
      <div v-if="!isUserLoggedIn" class="user-menu-container">
        <img
          :src="userIcon"
          alt="Iniciar Sesión"
          class="user-icon"
          @click="toggleUserMenu"
        />
        <transition name="fade">
          <div v-if="isUserMenuOpen" class="user-dropdown-menu">
            <router-link :to="{ name: 'Login' }" @click="closeUserMenu"
              >Ingresar</router-link
            >
            <router-link :to="{ name: 'Register' }" @click="closeUserMenu"
              >Registrarse</router-link
            >
            <router-link
              :to="{ name: 'RequestContactMe' }"
              @click="closeUserMenu"
              >Contáctenos</router-link
            >
          </div>
        </transition>
      </div>
      <!-- Menú para usuarios autenticados -->
      <div v-else class="authenticated-user-controls">
        <!-- Rectángulo de Puntos Disponibles (Desktop) -->
        <div v-if="isPointsUser" class="points-display-desktop">
          <span class="points-label">Puntos<br />disponibles</span>
          <div class="points-separator"></div>
          <span class="points-value">{{
            user.userPointsBalance.toLocaleString(locationFormat)
          }}</span>
        </div>
        <span class="user-name">Hola, {{ user.firstName }}</span>
        <!-- Menú de usuario con ícono para usuarios logueados -->
        <div class="user-menu-container">
          <img
            :src="userIcon"
            alt="Menú de usuario"
            class="user-icon"
            @click="toggleUserMenu"
          />
          <transition name="fade">
            <div v-if="isUserMenuOpen" class="user-dropdown-menu">
              <!-- Nuevas opciones para el usuario logueado -->
              <router-link
                :to="{ name: 'ReservationsSummary' }"
                @click="closeUserMenu"
                >Mis Reservas</router-link
              >
              <router-link
                :to="{ name: 'RequestContactMe' }"
                @click="closeUserMenu"
                >Contáctenos</router-link
              >
              <!-- Separador visual -->
              <div class="dropdown-divider"></div>
              <a href="#" @click.prevent="handleLogout">Salir</a>
            </div>
          </transition>
        </div>
      </div>
    </nav>

    <!-- Información de usuario en móvil (visible en el header) -->
    <div v-if="isUserLoggedIn" class="mobile-user-header-info">
      <div v-if="isPointsUser" class="mobile-points-pill">
        {{ user.userPointsBalance.toLocaleString(locationFormat) }} Pts
      </div>
      <span class="mobile-greeting">Hola, {{ user.firstName }}</span>
    </div>

    <!-- Icono de Hamburguesa (solo visible en móvil) -->
    <div class="hamburger-icon" @click="toggleMobileMenu">
      <span></span>
      <span></span>
      <span></span>
    </div>

    <!-- Panel del Menú Móvil -->
    <transition name="slide-left">
      <nav v-if="isMobileMenuOpen" class="mobile-menu">
        <ul>
          <!-- Selector de Moneda en Móvil -->
          <li v-if="showCurrencySelector" class="mobile-currency-item">
            <div class="currency-selector-mobile">
              <img :src="selectedCurrency === 'COP' ? coFlag : usFlag" class="currency-flag-icon" alt="Moneda" />
              <!-- Si se permite el cambio, mostramos el selector -->
              <select 
                v-if="canChangeCurrency"
                v-model="selectedCurrency" 
                @change="handleCurrencyChange" 
                class="currency-select-mobile"
              >
                <option value="COP">Peso (COP)</option>
                <option value="USD">Dólar (USD)</option>
              </select>
              <!-- Si NO se permite, mostramos un texto estático que no es manipulable -->
              <span v-else class="currency-value-mobile-readonly">
                {{ selectedCurrency === 'COP' ? 'Peso (COP)' : 'Dólar (USD)' }}
              </span>
            </div>
          </li>
          <li v-if="showCurrencySelector" class="mobile-menu-divider"></li>

          <!-- Opciones de autenticación condicionales (Movidio al inicio) -->
          <template v-if="!isUserLoggedIn">
            <li @click="navigateTo('/login')">Ingresar</li>
            <li @click="navigateTo('/register')">Registrarse</li>
          </template>
          <template v-else>
            <li class="user-name-mobile">Hola, {{ user.firstName }}</li>
            <li v-if="isPointsUser" class="points-display-mobile">
              <span class="points-label">Puntos Disponibles:</span>
              <span class="points-value">{{
                user.userPointsBalance.toLocaleString(locationFormat)
              }}</span>
            </li>
            <li @click="handleLogout">Salir</li>
          </template>
          <!--<li class="mobile-menu-divider"></li>-->
          <!-- Opciones de navegación principales -->
          <li @click="navigateTo({ name: 'RequestContactMe' })">Contáctenos</li>
          <li @click="navigateTo('/flights')">
            <img :src="flightIcon" alt="Vuelos" class="menu-icon" />
            Vuelos
          </li>
          <li @click="navigateTo('/hotels')">
            <img :src="hotelIcon" alt="Hoteles" class="menu-icon" />
            Hoteles
          </li>
          <li @click="navigateTo('/cars')">
            <img :src="carIcon" alt="Autos" class="menu-icon" />
            Autos
          </li>
          <li @click="navigateTo('/transfers')">
            <img :src="transferIcon" alt="Traslados" class="menu-icon" />
            Traslados
          </li>
          <li @click="navigateTo('/lounges')">
            <img :src="loungeIcon" alt="Salas VIP" class="menu-icon" />
            Salas VIP
          </li>
          <li @click="navigateTo('/assists')">
            <img :src="assistIcon" alt="Assist Card" class="menu-icon" />
            Assist Card
          </li>
          <li @click="navigateTo('/shows')">
            <img :src="showIcon" alt="Espectáculos" class="menu-icon" />
            Espectáculos
          </li>
        </ul>
      </nav>
    </transition>
    <!-- Overlay para cerrar el menú al hacer clic fuera -->
    <div
      v-if="isMobileMenuOpen"
      class="overlay"
      @click="toggleMobileMenu"
    ></div>
  </header>
</template>

<script>
import { useAuthStore } from "@/stores/authstore";
import { mapState, mapActions } from "pinia";
import userIcon from "@/assets/icons/icon_sesion.svg";
import flightIcon from "@/assets/images/plane-white-last.svg";
import hotelIcon from "@/assets/images/bed-white-last.svg";
import carIcon from "@/assets/images/car-white-last.svg";
import transferIcon from "@/assets/images/traslado-white-last.svg";
import loungeIcon from "@/assets/images/vip-white-last.svg";
import assistIcon from "@/assets/images/assist-white.svg";
import showIcon from "@/assets/images/espec-white-last.svg";
import coFlag from "@/assets/icons/CO.gif";
import usFlag from "@/assets/icons/US.gif";
import { startLoading, stopLoading } from "@/stores/loadingstore";

export default {
  name: "HeaderComp",
  props: {
    transparent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedCurrency: "",
      coFlag,
      usFlag,
      isCurrencyMenuOpen: false,
      isMobileMenuOpen: false,
      isUserMenuOpen: false,
      userIcon,
      flightIcon,
      hotelIcon,
      carIcon,
      transferIcon,
      loungeIcon,
      assistIcon,
      showIcon,
      locationFormat: process.env.VUE_APP_LOCATION_FORMAT,
    };
  },
  computed: {
    // Mapeamos el estado de autenticación y el usuario desde el store de Pinia
    ...mapState(useAuthStore, ["isUserLoggedIn", "user", "isPointsUser", "companySettings"]),
    showCurrencySelector() {
      // Si el usuario es de puntos, el selector siempre está oculto.
      if (this.isPointsUser) return false;
      // Para el resto de usuarios, ahora siempre se muestra para ver la selección actual.
      return true;
    },
    canChangeCurrency() {
      return this.$route.meta.allowCurrencyChange === true;
    }
  },
  methods: {
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      if (this.isMobileMenuOpen) {
        this.isCurrencyMenuOpen = false;
        this.isUserMenuOpen = false;
      }
    },
    toggleCurrencyMenu() {
      this.isCurrencyMenuOpen = !this.isCurrencyMenuOpen;
      if (this.isCurrencyMenuOpen) this.isUserMenuOpen = false;
    },
    toggleUserMenu() {
      this.isUserMenuOpen = !this.isUserMenuOpen;
      if (this.isUserMenuOpen) this.isCurrencyMenuOpen = false;
    },
    closeUserMenu() {
      this.isUserMenuOpen = false;
    },
    async selectCurrency(currency) {
      if (this.selectedCurrency === currency) {
        this.isCurrencyMenuOpen = false;
        return;
      }
      this.selectedCurrency = currency;
      this.isCurrencyMenuOpen = false;
      await this.handleCurrencyChange();
    },
    navigateTo(path) {
      // Evita errores si ya estamos en la ruta de destino
      if (this.$route.path !== path) {
        this.$router.push(path);
      } else if (path === "/login") this.$router.push(path);
      this.isMobileMenuOpen = false; // Cierra el menú después de navegar
      this.isUserMenuOpen = false;
      this.isCurrencyMenuOpen = false;
    },
    // Mapeamos la acción de logout
    ...mapActions(useAuthStore, ["logout", "changeCurrency"]),
    async handleCurrencyChange() {
      // Salvaguarda lógica: Si por manipulación del DOM se intenta disparar, 
      // verificamos de nuevo la regla en el código JS.
      if (!this.canChangeCurrency) {
        return;
      }
      const oldCurrency = this.companySettings.CurrencyType; // Guardamos la moneda actual antes del cambio
      try {
        startLoading();
        await this.changeCurrency(this.selectedCurrency); // La acción ahora actualiza el token en el store
        // Recargamos la página para forzar a las vistas de resultados a consultar precios con la nueva moneda
        window.location.reload();
      } catch (error) {
        console.error("Error al cambiar la moneda:", error);
        this.selectedCurrency = oldCurrency; // Revertimos la selección en caso de error
      } finally {
        stopLoading();
      }
    },
    async handleLogout() {
      const logoutUrl = this.logout(); // La acción ahora devuelve la URL.
      this.isMobileMenuOpen = false; // Cierra el menú si está abierto
      this.isUserMenuOpen = false;
      this.isCurrencyMenuOpen = false;

      if (logoutUrl) {
        // Si hay una URL de logout, redirigimos a ella.
        window.location.href = logoutUrl;
      } else {
        // Si no, redirigimos a la página de inicio como antes.
        this.$router.push({ name: "Home" });
      }
    },
    handleGlobalClick(e) {
      // Cierra los menús si el click es fuera de sus contenedores
      if (!e.target.closest('.user-menu-container') && !e.target.closest('.currency-menu-container')) {
        this.isUserMenuOpen = false;
        this.isCurrencyMenuOpen = false;
      }
    }
  },
  created() {
    // Establecemos el valor inicial basado en el CurrencyType del token decodificado
    this.selectedCurrency = this.companySettings.CurrencyType || "COP";
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
  },
};
</script>

<style scoped>
.header {
  /* Layering solution */
  position: absolute; /* Lo fijamos para que siempre esté visible */
  top: 0;
  left: 0;
  right: 0;
  z-index: 3; /* Capa más alta para estar siempre visible */

  /* Layout */
  display: flex;
  align-items: center;
  justify-content: space-between; /* Espacio entre logo y nav/hamburguesa */
  padding: 0 24px; /* Padding lateral */
  height: 80px; /* Altura fija para el header */

  /* Default appearance */
  background-color: #007bff; /* Fondo azul por defecto */
  color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;
}

/* Transparent state */
.header--transparent {
  background-color: transparent;
  box-shadow: none;
}

.logo {
  flex-shrink: 0;
  /* padding: 20px 0px 0px 100px; */ /* Eliminamos padding específico */
}

.header .logo img {
  height: 40px;
}

.desktop-nav {
  display: flex;
  margin-left: auto;
}

.desktop-nav ul {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 24px; /* Espacio entre elementos del menú */
}

.desktop-nav a {
  color: white;
  text-decoration: none;
  font-weight: bold;
  padding: 8px 0;
  border-bottom: 2px solid transparent;
  transition: border-color 0.2s;
}

.desktop-nav a:hover,
.desktop-nav a.router-link-exact-active {
  border-bottom-color: var(--color-acento-primario); /* Color de acento */
}

/* Contenedor para el nuevo menú de usuario con ícono */
.user-menu-container {
  position: relative;
  cursor: pointer;
}

.user-icon {
  height: 40px; /* Tamaño del ícono */
  width: 40px;
}

/* Menú desplegable */
.user-dropdown-menu {
  position: absolute;
  top: 100%; /* Posición justo debajo del ícono con un pequeño margen */
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 10;
  overflow: hidden; /* Para que los bordes redondeados afecten a los hijos */
  border: 1px solid #e9ecef;
}

.user-dropdown-menu a {
  display: block;
  padding: 12px 20px;
  color: var(--color-acento-secundario); /* Color corporativo */
  text-decoration: none;
  font-weight: normal;
  transition: background-color 0.2s;
  border-bottom: none; /* Quitamos el borde inferior de los links de nav */
}

.user-dropdown-menu a:hover {
  background-color: #f4f7f9; /* Color de fondo suave al pasar el mouse */
  border-bottom: none; /* Aseguramos que no aparezca el borde en hover */
}

/* Separador dentro del menú desplegable */
.dropdown-divider {
  height: 1px;
  background-color: #e9ecef; /* Un gris muy claro */
  margin: 8px 0; /* Espacio vertical */
}

.user-name {
  font-weight: normal;
  cursor: default;
}

/* --- Estilos del Selector de Moneda --- */
.currency-menu-container {
  position: relative;
  margin-right: 24px;
}

.currency-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.15);
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 40px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.currency-trigger:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

.currency-trigger--disabled {
  cursor: default;
  opacity: 0.8;
}
.currency-trigger--disabled:hover {
  background-color: rgba(255, 255, 255, 0.15); /* Mantenemos el fondo original */
}

.currency-flag-icon {
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 2px;
}

.currency-label-desktop {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
}

.arrow-icon {
  font-size: 0.8rem;
  transition: transform 0.3s;
  display: inline-block;
}

.arrow-icon--open {
  transform: rotate(180deg);
}

.currency-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 10;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.currency-option-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: var(--color-acento-secundario);
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.9rem;
}

.currency-option-item:hover {
  background-color: #f4f7f9;
}

.currency-option-item--active {
  background-color: #e9f5f9;
  font-weight: bold;
}

/* --- Selector de Moneda en Móvil --- */
.mobile-currency-item {
  padding: 10px 20px !important;
  background-color: rgba(255, 255, 255, 0.05);
}

.currency-selector-mobile {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.currency-select-mobile {
  background: transparent;
  color: white;
  border: none;
  font-weight: bold;
  font-size: 1.1rem;
  flex-grow: 1;
  outline: none;
}

.currency-value-mobile-readonly {
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  flex-grow: 1;
}

.currency-select-mobile option {
  color: #333;
}

.user-name-mobile {
  font-weight: normal;
  color: #ccc;
  cursor: default;
  background-color: transparent !important; /* Evita el efecto hover */
}

/* --- Hamburger Menu (Mobile) --- */
.hamburger-icon {
  display: none; /* Oculto por defecto en escritorio */
  cursor: pointer;
  z-index: 1001; /* Por encima del overlay */
  padding: 10px; /* Área de clic más grande */
}

.hamburger-icon span {
  display: block;
  width: 25px;
  height: 3px;
  margin: 5px 0;
  background-color: var(
    --color-acento-secundario
  ); /* Color corporativo oscuro */
  transition: 0.4s;
  border-radius: 3px;
}

/* Color del icono cuando el header es transparente */
.header--transparent .hamburger-icon span {
  background-color: white;
}

.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 250px;
  height: 100vh;
  background-color: var(--color-acento-terciario);
  z-index: 1000;
  padding-top: 80px; /* Espacio para el header */
  color: white;
  box-shadow: 2px 0 15px rgba(0, 0, 0, 0.2);
}

.mobile-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-menu li {
  padding: 15px 20px;
  font-size: 1.2rem;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
}

.menu-icon {
  width: 24px;
  height: 24px;
  margin-right: 15px;
}

.mobile-menu li:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.mobile-menu-divider {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 10px 20px;
  padding: 0 !important; /* Sobrescribir padding de li */
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* Transiciones para el menú */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}
.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}

/* Transición para el menú desplegable de usuario */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Contenedor para los elementos de usuario autenticado en desktop */
.authenticated-user-controls {
  display: flex;
  align-items: center;
  gap: 24px; /* Espacio entre el cuadro de puntos y la lista de usuario */
}
/* Estilos para el rectángulo de puntos en desktop */
.points-display-desktop {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid var(--color-acento-primario); /* Borde con color de acento */
  border-radius: 8px;
  padding: 6px 12px;
  color: white;
}

.points-display-desktop .points-label {
  font-weight: normal;
  font-size: 0.75rem; /* Letra más pequeña */
  line-height: 1.2; /* Ajuste para el texto de dos líneas */
  text-align: center;
}

.points-separator {
  width: 1px;
  height: 24px; /* Altura de la línea separadora */
  background-color: rgba(255, 255, 255, 0.5);
}

.points-display-desktop .points-value {
  font-weight: bold;
  font-size: 1.1rem;
}

/* Estilos para el rectángulo de puntos en móvil */
.points-display-mobile {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1); /* Fondo semitransparente */
  border-radius: 5px;
  padding: 10px 20px; /* Padding para que ocupe todo el ancho del li */
  font-size: 1rem;
  color: white;
  white-space: nowrap;
  justify-content: space-between; /* Espacio entre label y valor */
  border-bottom: 1px solid rgba(255, 255, 255, 0.1); /* Separador */
}
.points-display-mobile .points-label {
  font-weight: normal;
}
.points-display-mobile .points-value {
  font-weight: bold;
}

/* Estilos para la información de usuario en el header móvil */
.mobile-user-header-info {
  display: none; /* Oculto en desktop */
}

/* --- Responsive Design --- */
@media (max-width: 1024px) {
  .header {
    padding: 10px 16px; /* Ajustamos padding */
    flex-wrap: wrap; /* Permitimos que el contenido baje a otra línea */
    height: auto; /* Altura automática para adaptarse al contenido */
  }
  .desktop-nav {
    display: none; /* Ocultamos la navegación de escritorio */
  }
  .points-display-desktop {
    display: none; /* Ocultamos el display de puntos de desktop en móvil */
  }
  .logo {
    order: 1; /* Logo primero */
  }
  .hamburger-icon {
    display: block; /* Mostramos el icono de hamburguesa */
    order: 2; /* Hamburguesa segunda (misma línea que logo) */
  }

  .mobile-user-header-info {
    display: flex;
    flex-direction: row; /* Cambiamos a fila para ahorrar altura */
    align-items: center;
    justify-content: flex-end; /* Alineado a la derecha */
    width: 100%; /* Ocupa toda la nueva línea */
    order: 3; /* Baja a la siguiente línea */
    margin-top: 8px; /* Espacio con la fila de arriba */
    gap: 12px; /* Espacio entre puntos y nombre */
    color: white;
    font-size: 0.85rem;
  }

  .mobile-points-pill {
    background-color: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 2px 8px;
    font-weight: bold;
    font-size: 0.8rem;
    margin-bottom: 0;
    white-space: nowrap;
  }

  .mobile-greeting {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
}

@media (min-width: 1025px) {
  .header {
    padding-left: 104px;
    padding-right: 104px;
  }
}
</style>
